import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  useScormSession,
  useScormSessionSnapshot,
} from 'components/contentspace/scormInstance/ScormSessionContext'
import { clearPortalSession } from 'components/contentspace/sessionCookie'
import { testProps } from 'utils/test/qaData'

import PlayerHeader from '../../components/PlayerHeader'
import UserMenu from '../../components/UserMenu'
import { usePortalParams } from '../../hooks/usePortalParams'
import PlayerLoadingOverlay, { OverlayPhase } from './PlayerLoadingOverlay'
import * as s from './PublicPlayer.module.scss'
import { CompletionDialog } from './components/CompletionDialog/CompletionDialog'

interface ILiveCoursePlayerProps {
  courseName: string
  courseUrl: string
  hideHeader: boolean
  portalLandingPath: string
  portalRedirectUrl: string
  // Overlay coordination (consumed by PlayerLoadingOverlay in Task 5.7).
  // Accepted here so the launcher-emitted session_status / course_ready
  // don't need to be lifted out again — overlay reads the same state.
  queryLoading?: boolean
  sessionCreating?: boolean
  sessionReady?: boolean
}

type PlayerLocationMessage = {
  type: 'player_location'
  sectionId?: string | null
  blockId?: string | null
  channelId?: string
}

type SessionStatusMessage = {
  type: 'session_status'
  status: 'initialized' | 'terminated'
  channelId?: string
}

type CourseReadyMessage = {
  type: 'course_ready'
  channelId?: string
}

type LauncherInboundMessage = PlayerLocationMessage | SessionStatusMessage | CourseReadyMessage

const isLauncherMessage = (data: unknown): data is LauncherInboundMessage => {
  if (typeof data !== 'object' || data === null) {
    return false
  }
  const type = (data as { type?: unknown }).type
  return type === 'player_location' || type === 'session_status' || type === 'course_ready'
}

// Рендер активной SCORM-сессии. Подписан на ScormSession через
// useScormSessionSnapshot — ререндер триггерится ТОЛЬКО на явных `commit()`
// (настоящее изменение session) или на terminate. LMSSetValue в CMI не будит
// React — аккумулятор внутри ScormSession мутабельный.
export const PortalLmsPlayer: FC<ILiveCoursePlayerProps> = ({
  courseName,
  courseUrl,
  hideHeader,
  portalLandingPath,
  portalRedirectUrl,
  queryLoading = false,
  sessionCreating = false,
  sessionReady = true,
}) => {
  const { portalId, courseId } = usePortalParams()
  const history = useHistory()
  const scorm = useScormSession()
  const { session, terminated } = useScormSessionSnapshot()

  const [dialogDismissed, setDialogDismissed] = useState(false)
  // Phase B.2: launcher → portal message-derived state. Consumed by the
  // loading overlay state machine (Task 5.7).
  const [launcherSessionStatus, setLauncherSessionStatus] = useState<
    'initialized' | 'terminated' | null
  >(null)
  const [launcherCourseReady, setLauncherCourseReady] = useState(false)

  // Overlay-driven state. iframe stays hidden until phase === 'done'.
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [iframeErrored, setIframeErrored] = useState(false)
  const [overlayPhase, setOverlayPhase] = useState<OverlayPhase>('idle')

  // Launcher assigns a channelId per CrossDomainRouter instance. Captured from
  // the first inbound launcher message; echoed back on navigate_to so launcher
  // can drop stale messages from previous mounts (HMR, remount).
  const channelIdRef = useRef<string | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const showCompletionDialog = terminated && !dialogDismissed

  const handleCloseDialog = () => {
    setDialogDismissed(true)
    scorm.acknowledgeTerminate()
  }

  const handleBackToPortal = useCallback(() => {
    window.location.href = portalRedirectUrl
  }, [portalRedirectUrl])

  const handleLogout = useCallback(() => {
    if (portalId) {
      clearPortalSession(portalId)
    }
    window.location.href = portalRedirectUrl
  }, [portalId, portalRedirectUrl])

  // Launcher lives on portal origin in Phase B.2 (same-origin launcher wraps
  // possibly-cross-origin CDN iframe inside). Therefore origin check is against
  // window.location.origin — the outer iframe's origin == portal origin.
  useEffect(() => {
    const launcherOrigin = window.location.origin

    const handlePlayerMessage = (event: MessageEvent) => {
      if (event.origin !== launcherOrigin) {
        return
      }
      if (!isLauncherMessage(event.data)) {
        return
      }
      const data = event.data

      if (data.channelId && !channelIdRef.current) {
        channelIdRef.current = data.channelId
      }

      if (data.type === 'player_location') {
        if (!courseId) {
          return
        }
        const sectionId = data.sectionId ?? null
        const blockId = data.blockId ?? null
        const pathname = sectionId
          ? `/courses/${courseId}/sections/${sectionId}`
          : `/courses/${courseId}`
        const hash = blockId ? `#block-${blockId}` : ''
        history.replace(`${pathname}${hash}`)
      } else if (data.type === 'session_status') {
        setLauncherSessionStatus(data.status)
      } else if (data.type === 'course_ready') {
        setLauncherCourseReady(true)
      }
    }

    window.addEventListener('message', handlePlayerMessage)

    const handlePopState = () => {
      const channelId = channelIdRef.current
      if (!channelId) {
        return
      }
      const iframeWindow = iframeRef.current?.contentWindow
      if (!iframeWindow) {
        return
      }
      const pathMatch = window.location.pathname.match(/\/sections\/([^/]+)/)
      const sectionId = pathMatch ? pathMatch[1] : null
      const hashMatch = window.location.hash.match(/^#block-(.+)$/)
      const blockId = hashMatch ? hashMatch[1] : null
      iframeWindow.postMessage(
        {
          type: 'navigate_to',
          sectionId,
          blockId,
          channelId,
        },
        launcherOrigin,
      )
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('message', handlePlayerMessage)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [courseId, history])

  const driverUrl = courseUrl.replace(/\/[^/]+\.html$/, '/driver/index.html')

  return (
    <div className={s.root}>
      {!hideHeader && (
        <PlayerHeader
          backRedirectUrl={portalRedirectUrl}
          backUrl={portalLandingPath}
          courseName={courseName}
          isDebug={session.isDebug}
          progress={session.progress}
          userName={session.userName || session.email || undefined}
        />
      )}

      {hideHeader && (session.userName || session.email) && (
        <div className={s.floatingLogout}>
          <UserMenu onLogout={handleLogout}>
            <button className={s.floatingLogoutTrigger} title='User menu' type='button'>
              {session.userName || session.email}
            </button>
          </UserMenu>
        </div>
      )}

      <div className={s.content}>
        <iframe
          {...testProps({ el: 'iframe', name: 'portalLmsPlayer', sessionId: session.id })}
          allow='fullscreen *; autoplay *; encrypted-media *; picture-in-picture *'
          className={s.iframe}
          onError={() => setIframeErrored(true)}
          onLoad={() => setIframeLoaded(true)}
          ref={iframeRef}
          src={`${driverUrl}?csSessionId=${session.id}`}
          style={{ visibility: overlayPhase === 'done' ? 'visible' : 'hidden' }}
          title='Course Content'
          allowFullScreen
        />
        <PlayerLoadingOverlay
          iframeErrored={iframeErrored}
          iframeLoaded={iframeLoaded}
          launcherCourseReady={launcherCourseReady}
          launcherSessionStatus={launcherSessionStatus}
          onBackToPortal={handleBackToPortal}
          onPhaseChange={setOverlayPhase}
          queryLoading={queryLoading}
          sessionCreating={sessionCreating}
          sessionReady={sessionReady}
        />
      </div>

      {showCompletionDialog && (
        <CompletionDialog
          courseName={courseName}
          onClose={handleCloseDialog}
          portalLandingUrl={portalLandingPath}
          session={session}
        />
      )}
    </div>
  )
}
